export type SourceSchemaColumn = {
  name: string
  type?: string | null
}

export type SourceSchema = {
  datasetId?: string | null
  relationName?: string | null
  columns: SourceSchemaColumn[]
  schemaFingerprint: string
  sourceKind?: 'duckdb' | 'parquet' | 'motherduck' | 'unknown' | string | null
  extractIdentity?: string | null
  observedAtMs?: number | null
}

export const normalizeSourceColumnKey = (raw: any): string => {
  const text = String(raw ?? '').trim().toLowerCase()
  if (!text) return ''
  const unwrapped = text
    .replace(/^\[|\]$/g, '')
    .replace(/^"|"$/g, '')
    .trim()
  return unwrapped.replace(/[^a-z0-9_]+/g, '')
}

const stableStringify = (value: any): string => {
  const seen = new WeakSet<object>()
  const normalize = (current: any): any => {
    if (current === null || typeof current === 'undefined') return current
    if (typeof current === 'bigint') return current.toString()
    if (typeof current !== 'object') return current
    if (seen.has(current as object)) return '[Circular]'
    seen.add(current as object)
    if (Array.isArray(current)) return current.map(normalize)
    const out: Record<string, any> = {}
    for (const key of Object.keys(current).sort()) out[key] = normalize(current[key])
    return out
  }
  try {
    return JSON.stringify(normalize(value))
  } catch {
    return String(value ?? '')
  }
}

export const hashStringToBase36 = (raw: string): string => {
  const input = String(raw ?? '')
  let hash = 2166136261
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(36).padStart(7, '0')
}

export const buildSourceSchemaFingerprint = (args: {
  datasetId?: string | null
  relationName?: string | null
  columns: Array<SourceSchemaColumn | string | null | undefined>
  sourceKind?: string | null
  extractIdentity?: string | null
}): string => {
  const columns = (Array.isArray(args.columns) ? args.columns : [])
    .map((column) => {
      if (typeof column === 'string') {
        const name = column.trim()
        return name ? { name, key: normalizeSourceColumnKey(name), type: null } : null
      }
      const name = String(column?.name ?? '').trim()
      if (!name) return null
      return {
        name,
        key: normalizeSourceColumnKey(name),
        type: String(column?.type ?? '').trim().toLowerCase() || null,
      }
    })
    .filter((column): column is { name: string; key: string; type: string | null } => Boolean(column?.name && column?.key))
    .sort((a, b) => a.key.localeCompare(b.key) || a.name.localeCompare(b.name))
  return hashStringToBase36(stableStringify({
    datasetId: String(args.datasetId ?? '').trim() || null,
    relationName: String(args.relationName ?? '').trim() || null,
    sourceKind: String(args.sourceKind ?? '').trim() || null,
    extractIdentity: String(args.extractIdentity ?? '').trim() || null,
    columns,
  }))
}

export const buildSourceSchema = (args: {
  datasetId?: string | null
  relationName?: string | null
  columns: Array<SourceSchemaColumn | string | null | undefined>
  sourceKind?: SourceSchema['sourceKind']
  extractIdentity?: string | null
  observedAtMs?: number | null
}): SourceSchema => {
  const seen = new Set<string>()
  const columns: SourceSchemaColumn[] = []
  for (const column of Array.isArray(args.columns) ? args.columns : []) {
    const name = typeof column === 'string' ? column.trim() : String(column?.name ?? '').trim()
    if (!name) continue
    const key = normalizeSourceColumnKey(name)
    if (!key || seen.has(key)) continue
    seen.add(key)
    columns.push({
      name,
      type: typeof column === 'string' ? null : (String(column?.type ?? '').trim() || null),
    })
  }
  return {
    datasetId: String(args.datasetId ?? '').trim() || null,
    relationName: String(args.relationName ?? '').trim() || null,
    columns,
    schemaFingerprint: buildSourceSchemaFingerprint({
      datasetId: args.datasetId,
      relationName: args.relationName,
      columns,
      sourceKind: args.sourceKind,
      extractIdentity: args.extractIdentity,
    }),
    sourceKind: args.sourceKind ?? null,
    extractIdentity: String(args.extractIdentity ?? '').trim() || null,
    observedAtMs: Number.isFinite(Number(args.observedAtMs)) ? Number(args.observedAtMs) : null,
  }
}

export const sourceSchemaColumnNames = (schema: SourceSchema | null | undefined): string[] =>
  Array.isArray(schema?.columns)
    ? schema.columns.map((column) => String(column?.name ?? '').trim()).filter(Boolean)
    : []

export const isColumnInSourceSchema = (schema: SourceSchema | null | undefined, rawColumn: any): boolean => {
  const key = normalizeSourceColumnKey(rawColumn)
  if (!key) return false
  return sourceSchemaColumnNames(schema).some((column) => normalizeSourceColumnKey(column) === key)
}

export const assertNonEmptySourceSchemaForLocalExecution = (
  schema: SourceSchema | null | undefined,
  context?: string,
): SourceSchema => {
  if (!schema || sourceSchemaColumnNames(schema).length === 0) {
    throw new Error(`${context || 'local worksheet planning'}: source schema is required for local execution`)
  }
  return schema
}
